import { useCallback, useEffect, useMemo, useRef } from 'react';
import { clamp } from 'utils/number';
import { useDebouncedFieldAutosave } from 'hooks/useDebouncedFieldAutosave';
import type { EditorAutosaveDeps } from 'hooks/useAutosaveStatus';

export interface UseEbmProposalsEditorProps extends EditorAutosaveDeps {
  resultId: string | number | undefined;
  durationSeconds: number | undefined;
}

export interface UseEbmProposalsEditorReturn {
  handleEbmSelectionMapChange: (selection: Record<string, boolean>) => void;
  handleEbmDurationChange: (durationMinutes: number) => void;
  onEbmCodeToggle: () => void;
  pendingEbmSelection: () => Record<string, boolean> | null;
  pendingEbmDuration: () => number | null;
}

const clampedMinutes = (seconds: number | undefined) =>
  clamp(Math.round((seconds ?? 0) / 60), 1, 90);

export function useEbmProposalsEditor({
  resultId,
  durationSeconds,
  autosaveEditAsync,
  autosaveTimeouts,
  setAutoStatus,
  setHasUserEdits,
}: UseEbmProposalsEditorProps): UseEbmProposalsEditorReturn {
  const hasEbmSelectionInteractionRef = useRef(false);
  // Doubles as the pre-schedule dedupe key and the flush-time value to save; never reset once
  // set, since the last distinct selection is always the correct thing to (re-)flush.
  const pendingEbmSelectionRef = useRef<Record<string, boolean> | null>(null);

  useEffect(() => {
    hasEbmSelectionInteractionRef.current = false;
    pendingEbmSelectionRef.current = null;
  }, [resultId]);

  const persistEbmSelection = useCallback(
    (selection: Record<string, boolean>) => autosaveEditAsync({ ebmProposalSelection: selection }),
    [autosaveEditAsync]
  );
  const ebmSelectionAutosave = useDebouncedFieldAutosave(
    'ebmSelection',
    300,
    autosaveTimeouts,
    setAutoStatus,
    persistEbmSelection
  );

  const handleEbmSelectionMapChange = useCallback(
    (selection: Record<string, boolean>) => {
      if (!hasEbmSelectionInteractionRef.current) return;
      if (JSON.stringify(selection) === JSON.stringify(pendingEbmSelectionRef.current)) return;
      pendingEbmSelectionRef.current = selection;
      ebmSelectionAutosave.schedule(() => selection);
    },
    [ebmSelectionAutosave]
  );

  const pendingEbmSelection = useCallback((): Record<string, boolean> | null => {
    clearTimeout(autosaveTimeouts.current['ebmSelection']);
    return pendingEbmSelectionRef.current;
  }, [autosaveTimeouts]);

  const pendingEbmDurationRef = useRef<number | null>(null);

  const pendingEbmDuration = useCallback((): number | null => {
    const minutes = pendingEbmDurationRef.current;
    if (minutes === null) return null;
    clearTimeout(autosaveTimeouts.current['ebmDuration']);
    delete autosaveTimeouts.current['ebmDuration'];
    return minutes === clampedMinutes(durationSeconds) ? null : minutes;
  }, [autosaveTimeouts, durationSeconds]);

  const handleEbmDurationChange = useCallback(
    (durationMinutes: number) => {
      const key = 'ebmDuration';
      setHasUserEdits(true);
      setAutoStatus((prev) => ({ ...prev, [key]: 'typing' }));
      clearTimeout(autosaveTimeouts.current[key]);
      pendingEbmDurationRef.current = durationMinutes;

      autosaveTimeouts.current[key] = setTimeout(() => {
        pendingEbmDurationRef.current = null;
        if (durationMinutes === clampedMinutes(durationSeconds)) return;
        setAutoStatus((prev) => ({ ...prev, [key]: 'saving' }));
        void autosaveEditAsync({ durationSeconds: durationMinutes * 60 })
          .then(() => setAutoStatus((prev) => ({ ...prev, [key]: 'saved' })))
          .catch(() => setAutoStatus((prev) => ({ ...prev, [key]: 'error' })));
      }, 1000);
    },
    [autosaveEditAsync, durationSeconds, autosaveTimeouts, setAutoStatus, setHasUserEdits]
  );

  const onEbmCodeToggle = useCallback(() => {
    setHasUserEdits(true);
    hasEbmSelectionInteractionRef.current = true;
  }, [setHasUserEdits]);

  return useMemo(
    () => ({
      handleEbmSelectionMapChange,
      handleEbmDurationChange,
      onEbmCodeToggle,
      pendingEbmSelection,
      pendingEbmDuration,
    }),
    [
      handleEbmSelectionMapChange,
      handleEbmDurationChange,
      onEbmCodeToggle,
      pendingEbmSelection,
      pendingEbmDuration,
    ]
  );
}
